import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid2 as Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { LEAD_INTERESTS } from '@/types/api';
import { defaultLeadForm, leadFormSchema, type LeadFormValues } from './lead-form.schema';
import { useSubmitLead } from './use-submit-lead';
import { SubmitSuccess } from './SubmitSuccess';

export function ContactForm() {
  const { t } = useTranslation();
  const submit = useSubmitLead();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LeadFormValues>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: defaultLeadForm,
    mode: 'onTouched',
  });

  // Field errors carry i18n keys, so they translate with the rest of the page.
  const msg = (key?: string) => (key ? t(key) : undefined);

  if (submit.isSuccess) {
    return (
      <SubmitSuccess
        onReset={() => {
          submit.reset();
          reset(defaultLeadForm);
        }}
      />
    );
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit((v) => submit.mutateAsync(v))}>
      {/* Honeypot: not tabbable, invisible to assistive tech, and removed from
          layout with the standard clip technique. Note MUI's sx treats a bare
          `width: 1` as 100% — this field must never contribute any size. */}
      <Box
        component="input"
        {...register('website')}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          border: 0,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      />

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            {...register('name')}
            label={t('form.name')}
            required
            fullWidth
            autoComplete="name"
            error={Boolean(errors.name)}
            helperText={msg(errors.name?.message)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            {...register('email')}
            type="email"
            label={t('form.email')}
            required
            fullWidth
            autoComplete="email"
            error={Boolean(errors.email)}
            helperText={msg(errors.email?.message)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            {...register('phone')}
            label={t('form.phone')}
            fullWidth
            autoComplete="tel"
            error={Boolean(errors.phone)}
            helperText={msg(errors.phone?.message)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="interest"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label={t('form.interest')} fullWidth>
                {LEAD_INTERESTS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`interests.${key}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            {...register('street')}
            label={t('form.street')}
            fullWidth
            autoComplete="street-address"
          />
        </Grid>
        <Grid size={{ xs: 5, sm: 2 }}>
          <TextField
            {...register('postalCode')}
            label={t('form.postalCode')}
            fullWidth
            autoComplete="postal-code"
          />
        </Grid>
        <Grid size={{ xs: 7, sm: 4 }}>
          <TextField
            {...register('city')}
            label={t('form.city')}
            fullWidth
            autoComplete="address-level2"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            {...register('availability')}
            label={t('form.availability')}
            fullWidth
            helperText={t('form.availabilityHelper')}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            {...register('message')}
            label={t('form.message')}
            fullWidth
            multiline
            minRows={3}
            error={Boolean(errors.message)}
            helperText={msg(errors.message?.message)}
          />
        </Grid>

        <Grid size={12}>
          <Controller
            name="consent"
            control={control}
            render={({ field }) => (
              <>
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} size="small" />}
                  label={
                    <span>
                      {t('form.consent')}{' '}
                      <Link component={RouterLink} to="/datenschutz" target="_blank" underline="always">
                        {t('footer.privacy')}
                      </Link>
                    </span>
                  }
                  slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
                />
                {errors.consent && (
                  <FormHelperText error>{msg(errors.consent.message)}</FormHelperText>
                )}
              </>
            )}
          />
        </Grid>

        {submit.isError && (
          <Grid size={12}>
            <Alert severity="error">
              <strong>{t('form.errorTitle')}</strong> — {t('form.errorBody')}
            </Alert>
          </Grid>
        )}

        <Grid size={12}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              size="large"
              disabled={isSubmitting || submit.isPending}
              endIcon={<SendIcon />}
            >
              {submit.isPending ? t('form.submitting') : t('form.submit')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
